import { Quote } from "lucide-react";

import { Seo } from "@/components/Seo";
import { PageHero } from "@/components/PageHero";
import { Reveal } from "@/components/Reveal";
import { SmartImage } from "@/components/SmartImage";
import { CtaBand } from "@/components/Section";
import { IMAGES } from "@/data/site";

// Approved content — workbook Client Stories statements, verbatim.
const STORIES = [
  {
    no: "01",
    title: "The Details Matter",
    body: "An executive assistant at one client site reportedly expected an exceptionally detailed standard: no footprints after vacuuming, every chair pushed in, and every desk and chair lightly touched during service.",
    quote: "Some clients care about the smallest details. So do we.",
    image: IMAGES.floorScrubbing,
    alt: "Detailed commercial floor care in progress",
  },
  {
    no: "02",
    title: "Integrity When No One Is Watching",
    body: "One of our dealership associates found $4,000 accidentally left in cash under a desk. It was immediately turned into the Accounting Department. This wasn’t an isolated incident and to date we’ve recovered and returned a total of $95,000 to our clients.",
    quote: "Trust is earned in the moments no one expects.",
    image: IMAGES.moodMop,
    alt: "After-hours cleaning in a quiet commercial facility",
  },
];

const StoryChapter = ({ story, index }) => {
  const flip = index % 2 === 1;
  return (
    <section data-testid={`story-${story.no}`} className={index % 2 === 1 ? "bg-cool-gray" : "bg-white"}>
      <div className="container-px mx-auto max-w-[1500px] py-20 lg:py-28">
        <div className="grid items-center gap-12 lg:grid-cols-2 lg:gap-20">
          <Reveal className={flip ? "lg:order-2" : "lg:order-1"}>
            <SmartImage src={story.image} alt={story.alt} ratio="aspect-[4/3]" imgClassName="hover:scale-105" />
          </Reveal>

          <div className={flip ? "lg:order-1" : "lg:order-2"}>
            <Reveal>
              <span className="overline">Client Story {story.no}</span>
            </Reveal>
            <Reveal delay={0.05}>
              <h2 className="mt-4 font-display text-3xl font-bold leading-[1.05] tracking-tight text-navy sm:text-4xl lg:text-5xl">
                {story.title}
              </h2>
            </Reveal>
            <Reveal delay={0.1}>
              <p className="mt-6 text-lg leading-relaxed text-steel">{story.body}</p>
            </Reveal>
            <Reveal delay={0.15}>
              <figure className="mt-8 border-l-2 border-brand-green pl-6">
                <Quote size={22} className="text-brand-green" />
                <blockquote className="mt-3 font-display text-2xl font-semibold leading-snug tracking-tight text-navy sm:text-3xl">
                  “{story.quote}”
                </blockquote>
              </figure>
            </Reveal>
            {story.note && (
              <Reveal delay={0.2}>
                <p className="mt-6 max-w-xl border border-dashed border-border bg-white/60 p-4 text-xs leading-relaxed text-steel">
                  {story.note}
                </p>
              </Reveal>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};

const LongTermProof = () => (
  <section data-testid="story-longterm" className="relative overflow-hidden bg-navy noise-overlay">
    <div className="pointer-events-none absolute -right-24 top-0 h-96 w-96 rounded-full bg-brand-green/20 blur-3xl" />
    <div className="container-px relative z-[2] mx-auto max-w-[1200px] py-24 text-center lg:py-32">
      <Reveal>
        <span className="overline text-brand-green">Long-Term Relationship Proof</span>
      </Reveal>
      <Reveal delay={0.05}>
        <p className="mx-auto mt-8 max-w-4xl font-display text-3xl font-bold leading-[1.15] tracking-tight text-white [word-spacing:0.14em] sm:text-4xl lg:text-5xl">
          To date thirty seven years later we still maintain our first two customers.
        </p>
      </Reveal>
    </div>
  </section>
);

export default function ClientStories() {
  return (
    <>
      <Seo
        title="Client Stories & Proof"
        description="Real stories about detail, integrity and long-term relationships from Precision Maintenance1 LLC"
        path="/client-stories"
      />
      <PageHero eyebrow="Client Stories & Proof" title={["The stories behind", "the standard."]} />

      {STORIES.map((story, i) => (
        <StoryChapter key={story.no} story={story} index={i} />
      ))}

      <LongTermProof />

      <CtaBand
        title="Request a Customized Cleaning Plan"
        body=""
        primary={{ label: "Request a Customized Cleaning Plan", to: "/request-a-quote" }}
        secondary={{ label: "Talk With Our Team", to: "/contact" }}
      />
    </>
  );
}
